import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Loader2 } from 'lucide-react';
import { toast, Toaster } from 'sonner';
import api from '../../../../services/api';

export default function TaskForm() {
    const { id } = useParams();
    const navigate = useNavigate();
    const isEdit = !!id;

    const [form, setForm] = useState({ title: '', notes: '', due_date: '', assigned_to: '', lead_id: '' });
    const [users, setUsers] = useState([]);
    const [leads, setLeads] = useState([]);
    const [loading, setLoading] = useState(isEdit);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        // Fetch supporting data
        api.get('/staff-list').then(r => setUsers(r.data)).catch(() => {});
        api.get('/leads').then(r => setLeads(r.data?.data || r.data || [])).catch(() => {});

        // Fetch task details if editing
        if (isEdit) {
            api.get(`/tasks/${id}`)
                .then(r => {
                    const t = r.data;
                    setForm({
                        title: t.title || '',
                        notes: t.notes || '',
                        due_date: t.due_date ? t.due_date.split('T')[0] : '',
                        assigned_to: t.assigned_to || '',
                        lead_id: t.lead_id || '',
                    });
                })
                .catch(() => toast.error('Failed to load task'))
                .finally(() => setLoading(false));
        }
    }, [id, isEdit]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            const payload = { ...form };
            if (!payload.assigned_to) delete payload.assigned_to;
            if (!payload.lead_id) delete payload.lead_id;
            if (!payload.due_date) delete payload.due_date;

            if (isEdit) {
                await api.put(`/tasks/${id}`, payload);
                toast.success('Task updated');
            } else {
                await api.post('/tasks', payload);
                toast.success('Task created');
            }
            setTimeout(() => navigate('/admin/tasks'), 800);
        } catch {
            toast.error(isEdit ? 'Failed to update task' : 'Failed to create task');
        } finally {
            setSaving(false);
        }
    };

    if (loading) return <div className="flex justify-center items-center min-h-screen"><Loader2 className="w-10 h-10 animate-spin text-blue-600" /></div>;

    return (
        <div>
            <Toaster position="top-right" richColors />
            <div className="max-w-xl mx-auto">
                <div className="flex items-center gap-3 mb-6">
                    <button onClick={() => navigate('/admin/tasks')} className="p-2 hover:bg-gray-200 rounded-lg"><ArrowLeft className="w-5 h-5" /></button>
                    <h2 className="text-2xl font-bold text-gray-900">{isEdit ? 'Edit Task' : 'Add Task'}</h2>
                </div>
                <form onSubmit={handleSubmit} className="bg-white rounded-xl border border-gray-200 shadow-sm p-6 space-y-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Title <span className="text-red-500">*</span></label>
                        <input 
                            type="text" 
                            value={form.title} 
                            onChange={e => setForm(p => ({ ...p, title: e.target.value }))} 
                            required 
                            style={{ color: '#000000', backgroundColor: '#ffffff' }}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm text-black bg-white focus:outline-none focus:ring-2 focus:ring-blue-500" 
                        />
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Due Date</label>
                            <input 
                                type="date" 
                                value={form.due_date} 
                                onChange={e => setForm(p => ({ ...p, due_date: e.target.value }))} 
                                style={{ color: '#000000', backgroundColor: '#ffffff', colorScheme: 'light' }}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm text-black bg-white focus:outline-none focus:ring-2 focus:ring-blue-500" 
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Assign To</label>
                            <select 
                                value={form.assigned_to} 
                                onChange={e => setForm(p => ({ ...p, assigned_to: e.target.value }))} 
                                style={{ color: '#000000', backgroundColor: '#ffffff', colorScheme: 'light' }}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm text-black bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 cursor-pointer"
                            >
                                <option value="">Unassigned</option>
                                {users.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
                            </select>
                        </div>
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Linked Lead</label>
                        <select 
                            value={form.lead_id} 
                            onChange={e => setForm(p => ({ ...p, lead_id: e.target.value }))} 
                            style={{ color: '#000000', backgroundColor: '#ffffff', colorScheme: 'light' }}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm text-black bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 cursor-pointer"
                        >
                            <option value="">None</option>
                            {leads.map(l => <option key={l.id} value={l.id}>{l.name} ({l.phone})</option>)}
                        </select>
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                        <textarea 
                            value={form.notes} 
                            onChange={e => setForm(p => ({ ...p, notes: e.target.value }))} 
                            rows={3} 
                            style={{ color: '#000000', backgroundColor: '#ffffff' }}
                            placeholder="Add call notes, verification details, etc."
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm text-black bg-white focus:outline-none focus:ring-2 focus:ring-blue-500" 
                        />
                    </div>
                    <div className="flex gap-3 pt-2">
                        <button type="submit" disabled={saving} className="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition disabled:opacity-50">
                            {saving ? 'Saving…' : isEdit ? 'Update Task' : 'Create Task'}
                        </button>
                        <button type="button" onClick={() => navigate('/admin/tasks')} className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50">Cancel</button>
                    </div>
                </form>
            </div>
        </div>
    );
}
